import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { STORAGE_CART_KEY } from '../config';

const CartContext = createContext(null);

const readStoredCart = () => {
  try {
    const raw = localStorage.getItem(STORAGE_CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

// Carrito de compras de la tienda publica: vive en localStorage para sobrevivir
// recargas de pagina y permitir agregar productos sin haber iniciado sesion
// (la sesion solo se exige al llegar al checkout, ver RequireAuth en routes/index.js).
export function CartProvider({ children }) {
  const [items, setItems] = useState(readStoredCart);
  // Panel del carrito (ver sections/client/cart/CartDrawer): su estado vive aca para que
  // cualquier "Agregar al carrito" (catalogo, home) pueda abrirlo tras agregar un producto.
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_CART_KEY, JSON.stringify(items));
  }, [items]);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const addItem = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      return [...prev, { product, quantity }];
    });
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    const parsed = Math.max(1, Number(quantity) || 1);
    setItems((prev) => prev.map((item) => (item.product.id === productId ? { ...item, quantity: parsed } : item)));
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((item) => item.product.id !== productId));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((sum, item) => sum + item.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, addItem, updateQuantity, removeItem, clear, count, isDrawerOpen, openDrawer, closeDrawer }),
    [items, addItem, updateQuantity, removeItem, clear, count, isDrawerOpen, openDrawer, closeDrawer]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

CartProvider.propTypes = {
  children: PropTypes.node,
};

export default CartContext;
